import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { AdminTicketListItemDto } from '@ivy-monorepo/shared'
import Table from '../../components/Table'
import Pagination from '../../components/Pagination'
import { adminService } from '../../services/admin.service'
import { useSearch } from '../../contexts/SearchContext'
import { formatTicketPrice } from '../../utils/ticketPrice'
import { formatEventSource } from '../../utils/eventJourney'
import RefundRequestsTab from './RefundRequestsTab'

type TicketsTab = 'tickets' | 'refunds'

function calculateOptimalPageSize(): number {
  const viewportHeight = window.innerHeight
  const rowHeight = 56
  const headerHeight = 220
  const paginationHeight = 60
  const availableHeight = viewportHeight - headerHeight - paginationHeight
  const calculatedPageSize = Math.floor(availableHeight / rowHeight)
  return Math.max(10, Math.min(100, calculatedPageSize))
}

const TicketsPage = () => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const activeTab: TicketsTab = searchParams.get('tab') === 'refunds' ? 'refunds' : 'tickets'

  const handleTabChange = (tab: TicketsTab) => {
    navigate(tab === 'refunds' ? '?tab=refunds' : '?')
  }

  const tabs = (
    <div style={{ display: 'flex', gap: '24px' }}>
      {(['tickets', 'refunds'] as TicketsTab[]).map(tab => (
        <button
          key={tab}
          type="button"
          className={`tab ${tab === activeTab ? 'active' : ''}`}
          onClick={() => handleTabChange(tab)}
        >
          {tab === 'tickets' ? 'Tickets' : 'Refunds'}
        </button>
      ))}
    </div>
  )
  const { searchValue } = useSearch()

  const [tickets, setTickets] = useState<AdminTicketListItemDto[]>([])
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    pageSize: 10,
    totalPages: 0,
    hasNext: false,
    hasPrev: false,
  })
  const [debouncedSearchValue, setDebouncedSearchValue] = useState(searchValue)

  const currentPage = parseInt(searchParams.get('page') || '1')
  const urlPageSize = searchParams.get('pageSize')
  const currentPageSize = urlPageSize ? parseInt(urlPageSize) : calculateOptimalPageSize()
  const currentSortBy = searchParams.get('sortBy') || 'purchasedAt'
  const currentSortOrder = searchParams.get('sortOrder') || 'desc'

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearchValue(searchValue), 300)
    return () => clearTimeout(timer)
  }, [searchValue])

  const fetchTickets = useCallback(async () => {
    try {
      const response = await adminService.getTickets(
        currentPage,
        currentPageSize,
        debouncedSearchValue,
        currentSortBy,
        currentSortOrder as 'asc' | 'desc',
      )
      setTickets(
        response.tickets.map(ticket => ({
          ...ticket,
          priceLabel:
            ticket.quantity > 1
              ? `${ticket.quantity} × ${formatTicketPrice(ticket.priceCents)} = ${formatTicketPrice(ticket.totalCents)}`
              : formatTicketPrice(ticket.totalCents),
          sourceLabel: formatEventSource(ticket.source),
        })),
      )
      setPagination(response.pagination)
    } catch (err) {
      console.log('Failed to fetch tickets', err)
    }
  }, [currentPage, currentPageSize, debouncedSearchValue, currentSortBy, currentSortOrder])

  useEffect(() => {
    fetchTickets()
  }, [fetchTickets])

  const handlePageChange = (page: number) => {
    const newParams = new URLSearchParams(searchParams)
    newParams.set('page', page.toString())
    navigate(`?${newParams.toString()}`)
  }

  const handleSortChange = (sortBy: string, sortOrder: string) => {
    const newParams = new URLSearchParams(searchParams)
    newParams.set('sortBy', sortBy)
    newParams.set('sortOrder', sortOrder)
    newParams.set('page', '1')
    navigate(`?${newParams.toString()}`)
  }

  const columns = useMemo(
    () => [
      { label: '', accessor: 'user.imageUrl', type: 'userAvatar' },
      { label: 'User', accessor: 'user.lastName', type: 'fullNameAndTelephone' },
      { label: 'Event', accessor: 'event.name', type: 'text' },
      { label: 'Venue', accessor: 'venue.name', type: 'text' },
      { label: 'Event starts', accessor: 'eventStartDate', type: 'datetime' },
      { label: 'Price', accessor: 'priceLabel', type: 'text' },
      { label: 'Status', accessor: 'status', type: 'text' },
      { label: 'Source', accessor: 'sourceLabel', type: 'text' },
      { label: 'Square payment', accessor: 'squarePaymentId', type: 'text' },
      { label: 'Purchased', accessor: 'purchasedAt', type: 'datetime' },
    ],
    [],
  )

  if (activeTab === 'refunds') {
    return <RefundRequestsTab tabs={tabs} />
  }

  return (
    <div className="users-container">
      <div className="users-header">
        <h2>Tickets</h2>
        {pagination.totalPages > 1 && (
          <Pagination currentPage={pagination.page} totalPages={pagination.totalPages} onPageChange={handlePageChange} />
        )}
      </div>
      <div className="tab-bar" style={{ justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        {tabs}
      </div>

      <div className="users-table-container">
        <div className="table-wrapper">
          <Table
            id="tickets"
            name="Tickets"
            keyField="publicId"
            initialSortColumn={currentSortBy}
            initialSortOrder={currentSortOrder}
            columns={columns}
            sourceData={tickets}
            isPropertyBarVisible={false}
            filterTerms=""
            onRowClick={() => {}}
            onSortChange={handleSortChange}
            disableGlobalSearch={true}
            disableClientSort={true}
          />
        </div>
      </div>
    </div>
  )
}

export default TicketsPage
